import { useState } from 'react';
import { QrCode, Download, Copy, Check, Link, Type, Wifi, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const QRCodeGenerator = () => {
    useToolUsage('qr_code_generator'); // Track tool usage
    const [inputValue, setInputValue] = useState('');
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [activeTab, setActiveTab] = useState('url');
    const [copied, setCopied] = useState(false);

    // WiFi specific states
    const [wifiSSID, setWifiSSID] = useState('');
    const [wifiPassword, setWifiPassword] = useState('');
    const [wifiEncryption, setWifiEncryption] = useState('WPA');

    const generateQRCode = (data: string) => {
        if (!data.trim()) {
            toast.error('Please enter some data');
            return;
        }
        // Using QR Server API (free, no auth required)
        const encodedData = encodeURIComponent(data);
        const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedData}`;
        setQrCodeUrl(url);
        toast.success('QR Code generated!');
    };

    const handleGenerate = () => {
        let data = '';

        switch (activeTab) {
            case 'url':
                data = inputValue;
                break;
            case 'text':
                data = inputValue;
                break;
            case 'wifi':
                data = `WIFI:T:${wifiEncryption};S:${wifiSSID};P:${wifiPassword};;`;
                break;
            case 'email':
                data = `mailto:${inputValue}`;
                break;
            case 'phone':
                data = `tel:${inputValue}`;
                break;
            default:
                data = inputValue;
        }

        generateQRCode(data);
    };

    const downloadQRCode = async () => {
        if (!qrCodeUrl) return;

        try {
            const response = await fetch(qrCodeUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'qrcode.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('QR Code downloaded!');
        } catch (error) {
            toast.error('Failed to download');
        }
    };

    const copyToClipboard = async () => {
        if (!qrCodeUrl) return;

        try {
            await navigator.clipboard.writeText(qrCodeUrl);
            setCopied(true);
            toast.success('URL copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error('Failed to copy');
        }
    };

    const tabConfig = [
        { id: 'url', label: 'URL', icon: Link, placeholder: 'https://example.com' },
        { id: 'text', label: 'Text', icon: Type, placeholder: 'Enter any text...' },
        { id: 'wifi', label: 'WiFi', icon: Wifi, placeholder: '' },
        { id: 'email', label: 'Email', icon: Mail, placeholder: 'email@example.com' },
        { id: 'phone', label: 'Phone', icon: Phone, placeholder: '+1234567890' },
    ];

    return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                        <QrCode className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">QR Code Generator</h1>
                        <p className="text-muted-foreground">Create QR codes for URLs, text, WiFi, and more</p>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {/* Input Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>Generate QR Code</CardTitle>
                        <CardDescription>Choose a type and enter your data</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="grid grid-cols-5 mb-6">
                                {tabConfig.map((tab) => (
                                    <TabsTrigger key={tab.id} value={tab.id} className="text-xs">
                                        <tab.icon className="h-4 w-4" />
                                    </TabsTrigger>
                                ))}
                            </TabsList>

                            {tabConfig.filter(t => t.id !== 'wifi').map((tab) => (
                                <TabsContent key={tab.id} value={tab.id}>
                                    <div className="space-y-4">
                                        <div>
                                            <Label>{tab.label}</Label>
                                            <Input
                                                placeholder={tab.placeholder}
                                                value={inputValue}
                                                onChange={(e) => setInputValue(e.target.value)}
                                                className="mt-2"
                                            />
                                        </div>
                                    </div>
                                </TabsContent>
                            ))}

                            <TabsContent value="wifi">
                                <div className="space-y-4">
                                    <div>
                                        <Label>Network Name (SSID)</Label>
                                        <Input
                                            placeholder="MyWiFiNetwork"
                                            value={wifiSSID}
                                            onChange={(e) => setWifiSSID(e.target.value)}
                                            className="mt-2"
                                        />
                                    </div>
                                    <div>
                                        <Label>Password</Label>
                                        <Input
                                            type="password"
                                            placeholder="WiFi password"
                                            value={wifiPassword}
                                            onChange={(e) => setWifiPassword(e.target.value)}
                                            className="mt-2"
                                        />
                                    </div>
                                    <div>
                                        <Label>Encryption</Label>
                                        <select
                                            value={wifiEncryption}
                                            onChange={(e) => setWifiEncryption(e.target.value)}
                                            className="w-full mt-2 p-2 rounded-md border bg-background"
                                        >
                                            <option value="WPA">WPA/WPA2</option>
                                            <option value="WEP">WEP</option>
                                            <option value="nopass">None</option>
                                        </select>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>

                        <Button onClick={handleGenerate} className="w-full mt-6">
                            <QrCode className="h-4 w-4 mr-2" />
                            Generate QR Code
                        </Button>
                    </CardContent>
                </Card>

                {/* Preview Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>Preview</CardTitle>
                        <CardDescription>Your generated QR code</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center">
                        {qrCodeUrl ? (
                            <>
                                <div className="p-4 bg-white rounded-xl shadow-lg mb-6">
                                    <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
                                </div>
                                <div className="flex gap-3 w-full">
                                    <Button onClick={downloadQRCode} className="flex-1">
                                        <Download className="h-4 w-4 mr-2" />
                                        Download
                                    </Button>
                                    <Button variant="outline" onClick={copyToClipboard} className="flex-1">
                                        {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                                        {copied ? 'Copied!' : 'Copy URL'}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="w-64 h-64 border-2 border-dashed border-border rounded-xl flex items-center justify-center">
                                <div className="text-center text-muted-foreground">
                                    <QrCode className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">QR code will appear here</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default QRCodeGenerator;
